import React, { useEffect, useState } from "react";
import { useAuth, BACKEND_URL } from "@/App";
import { toast } from "sonner";
import { X, Euro, AlertTriangle, FileText, Calendar, Share2, Download } from "lucide-react";

/**
 * Modal that displays a client's estratto conto (open invoices) detail.
 * Opens when user taps the "€ da pagare" banner in giri.
 */
export function EstrattoContoModal({ clientId, clientName, onClose, onToggleChanged }) {
  const { authAxios, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    setLoading(true);
    authAxios
      .get(`/estratto-conto/client/${clientId}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Errore caricamento estratto conto");
        onClose();
      })
      .finally(() => setLoading(false));
  }, [clientId, authAxios, onClose]);

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try {
      const d = new Date(iso);
      return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "2-digit" });
    } catch {
      return iso;
    }
  };

  const isOverdue = (scadenza) => {
    if (!scadenza) return false;
    try {
      return new Date(scadenza) < new Date();
    } catch {
      return false;
    }
  };

  const fmtEur = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });

  // Sort invoices by due date ascending (oldest overdue first)
  const sortedInvoices = React.useMemo(() => {
    if (!data?.invoices) return [];
    return [...data.invoices].sort((a, b) => {
      if (!a.scadenza) return 1;
      if (!b.scadenza) return -1;
      return a.scadenza.localeCompare(b.scadenza);
    });
  }, [data]);

  // Share or download the client's estratto conto PDF
  const handleShare = async () => {
    if (sharing) return;
    setSharing(true);
    const tid = toast.loading("Preparo il PDF...");
    try {
      // Use fetch with credentials (cookie auth) instead of axios with responseType:'blob'
      // to avoid issues with some XHR interceptors that try to read responseText on blobs.
      const res = await fetch(`${BACKEND_URL}/api/estratto-conto/client/${clientId}/pdf`, {
        credentials: "include",
      });
      if (!res.ok) {
        let msg = "Errore generazione PDF";
        try {
          const j = await res.json();
          if (j?.detail) msg = j.detail;
        } catch { /* non-JSON response, keep default */ }
        throw new Error(msg);
      }
      const blob = await res.blob();
      const filename = `EstrattoConto_${(clientName || "cliente").replace(/[^\w\-]+/g, "_").slice(0, 40)}.pdf`;
      const file = new File([blob], filename, { type: "application/pdf" });

      // Prefer native Web Share API on mobile (iOS/Android can send to WhatsApp, Email, etc.)
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        toast.dismiss(tid);
        try {
          await navigator.share({
            files: [file],
            title: "Estratto Conto",
            text: `Estratto conto di ${clientName}`,
          });
          toast.success("Condivisione avviata");
          return;
        } catch (err) {
          if (err?.name === "AbortError") return; // user cancelled — nothing else to do
          // iOS often raises NotAllowedError / SecurityError if the user gesture
          // expired during the fetch — fall through to the download fallback
          // so the user still gets the PDF.
        }
      }
      // Fallback: trigger a download (works on every browser)
      toast.dismiss(tid);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF scaricato");
    } catch (err) {
      toast.error(err.message || "Errore generazione PDF", { id: tid });
    } finally {
      setSharing(false);
    }
  };

  // Detect if Web Share API is available (on mobile browsers)
  const hasWebShare = typeof navigator !== "undefined" &&
    typeof navigator.canShare === "function" &&
    navigator.canShare({ files: [new File([""], "x.pdf", { type: "application/pdf" })] });

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      data-testid="estratto-conto-modal"
    >
      <div
        className="bg-[#18181b] border border-white/10 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <Euro className="w-5 h-5 text-orange-400 shrink-0" />
            <div className="min-w-0">
              <h3 className="text-white font-bold text-base truncate">Estratto Conto</h3>
              <p className="text-zinc-500 text-xs truncate">{clientName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {user?.role === "admin" && data && (
              <button
                onClick={async () => {
                  if (toggling) return;
                  const newVal = !data.escluso_estratto_conto;
                  setToggling(true);
                  try {
                    await authAxios.put(`/clients/${clientId}/toggle-estratto-conto`, { escluso: newVal });
                    setData({ ...data, escluso_estratto_conto: newVal });
                    toast.success(newVal ? "Cliente escluso dai Giri" : "Cliente re-incluso nei Giri");
                    onToggleChanged && onToggleChanged();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Errore");
                  } finally {
                    setToggling(false);
                  }
                }}
                disabled={toggling}
                data-testid="modal-toggle-escluso-ec"
                title={data.escluso_estratto_conto
                  ? "Cliente escluso dai Giri — tocca per re-includere"
                  : "Cliente attivo nei Giri — tocca per escludere"}
                className={`px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-colors disabled:opacity-50 ${
                  data.escluso_estratto_conto
                    ? "border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25"
                    : "border-emerald-500/40 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                }`}
              >
                {data.escluso_estratto_conto ? "Escluso" : "Attivo"}
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded text-zinc-400 hover:text-white"
              data-testid="close-estratto-modal"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-orange-500 mx-auto" />
            <p className="text-zinc-500 text-xs mt-3">Caricamento...</p>
          </div>
        ) : !data ? null : (
          <>
            {/* Totals banner */}
            <div className="p-4 border-b border-white/10 bg-gradient-to-r from-orange-500/10 to-red-500/5">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Importo</p>
                  <p className="text-white font-mono text-sm font-bold" data-testid="tot-importo">
                    {fmtEur(data.totale_importo)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Acconto</p>
                  <p className="text-emerald-400 font-mono text-sm font-bold" data-testid="tot-acconto">
                    {fmtEur(data.totale_acconto)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-zinc-400 uppercase tracking-wider">Saldo</p>
                  <p className="text-orange-400 font-mono text-base font-bold" data-testid="tot-saldo">
                    {fmtEur(data.totale_saldo)}
                  </p>
                </div>
              </div>
              <p className="text-zinc-500 text-[10px] text-center mt-2">
                {data.n_fatture} fattur{data.n_fatture === 1 ? "a" : "e"} da incassare
              </p>
              <button
                type="button"
                onClick={handleShare}
                disabled={sharing}
                data-testid="share-ec-pdf"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold transition-colors active:scale-[0.98]"
              >
                {sharing ? (
                  <>
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Preparo...
                  </>
                ) : hasWebShare ? (
                  <>
                    <Share2 className="w-4 h-4" />
                    Condividi PDF con cliente
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4" />
                    Scarica PDF estratto conto
                  </>
                )}
              </button>
            </div>

            {/* Invoices list */}
            <div className="flex-1 overflow-y-auto p-2">
              {sortedInvoices.length === 0 ? (
                <div className="text-center py-10">
                  <FileText className="w-10 h-10 text-zinc-700 mx-auto mb-2" />
                  <p className="text-zinc-500 text-sm">Nessuna fattura</p>
                </div>
              ) : (
                sortedInvoices.map((inv) => {
                  const overdue = isOverdue(inv.scadenza);
                  return (
                    <div
                      key={`inv-${inv.numero}-${inv.data_doc || ""}`}
                      data-testid={`invoice-row-${inv.numero}`}
                      className={`p-3 mb-1.5 rounded-lg border flex items-start gap-3 ${
                        overdue
                          ? "border-red-500/30 bg-red-500/5"
                          : "border-white/5 bg-[#0f0f11]"
                      }`}
                    >
                      {overdue && (
                        <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-1" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <p className="text-white text-sm font-mono font-semibold">
                            #{inv.numero}
                          </p>
                          <span className="text-zinc-500 text-[10px]">{inv.tipo}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px] text-zinc-400 mt-0.5">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            emessa {fmtDate(inv.data_doc)}
                          </span>
                          <span className={overdue ? "text-red-400 font-semibold" : ""}>
                            scade {fmtDate(inv.scadenza)}
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-white font-mono text-sm font-bold">
                          {fmtEur(inv.saldo)}
                        </p>
                        {inv.acconto > 0 && (
                          <p className="text-emerald-400 text-[10px] font-mono">
                            acc. {fmtEur(inv.acconto)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
